import { type LogEntryKind, type LogLine } from '@aiai/shared';

export interface TimelineLine extends LogLine { seq: number }

export interface ToolCall {
  name?: string;        // tool_use name; undefined for orphan results
  detail?: string;      // formatted input
  result?: string;      // paired tool_result detail
  isError?: boolean;
  key: string;
}

export type TimelineItem =
  | { kind: 'prose'; text: string; ts: string; key: string }
  | { kind: 'thinking'; text: string; ts: string; key: string }
  | { kind: 'stage'; text: string; ts: string; key: string }
  | { kind: 'gate'; text: string; isError: boolean; ts: string; key: string }
  | { kind: 'system'; text: string; ts: string; key: string }
  | { kind: 'tools'; tools: ToolCall[]; summary: string; ts: string; key: string };

function classifyLegacy(text: string, stream: LogLine['stream']): LogEntryKind {
  if (stream !== 'stdout') return 'system';
  if (text.startsWith('  \u{1F527} ')) return 'tool_use';
  if (text.startsWith('     ↳')) return 'tool_result';
  if (text.startsWith('● ')) return 'system';
  return 'prose';
}

interface Fragment { kind: LogEntryKind; text: string; ts: string; seq: number; sub: number;
  legacy: boolean; tool?: string; detail?: string; isError?: boolean }

function toFragments(line: TimelineLine): Fragment[] {
  if (line.kind) {
    return [{ kind: line.kind, text: line.text, ts: line.ts, seq: line.seq, sub: 0,
      legacy: false, tool: line.meta?.tool, detail: line.meta?.detail, isError: line.meta?.isError }];
  }
  return line.text.split('\n').map((t, sub) => {
    const kind = classifyLegacy(t, line.stream);
    return {
      kind, text: t, ts: line.ts, seq: line.seq, sub,
      legacy: true,
      tool: undefined,
      detail: kind === 'tool_use' ? t.slice('  \u{1F527} '.length)
        : kind === 'tool_result' ? t.replace(/^ {5}↳ ?/, '') : undefined,
    };
  });
}

function toolSummary(tools: ToolCall[]): string {
  const named: string[] = [];
  const counts = new Map<string, number>();
  let orphans = 0;
  for (const t of tools) {
    if (!t.name) { orphans++; continue; }
    if (!counts.has(t.name)) named.push(t.name);
    counts.set(t.name, (counts.get(t.name) ?? 0) + 1);
  }
  const parts = named.map((n) => (counts.get(n)! > 1 ? `${n} ×${counts.get(n)}` : n));
  if (orphans > 0) parts.push(`${orphans} result${orphans !== 1 ? 's' : ''}`);
  return parts.join(' · ');
}

export function buildTimeline(lines: TimelineLine[]): TimelineItem[] {
  const items: TimelineItem[] = [];
  let group: { tools: ToolCall[]; ts: string; key: string } | null = null;

  const flush = () => {
    if (!group) return;
    items.push({ kind: 'tools', tools: group.tools, summary: toolSummary(group.tools), ts: group.ts, key: group.key });
    group = null;
  };

  for (const line of lines) {
    for (const f of toFragments(line)) {
      const fkey = `${f.seq}-${f.sub}`;
      if (f.kind === 'tool_use' || f.kind === 'tool_result') {
        if (!group) group = { tools: [], ts: f.ts, key: `tools-${fkey}` };
        if (f.kind === 'tool_use') {
          // structured: name comes from meta.tool only. legacy: first word of
          // detail is the name, the rest is the detail.
          const name = f.legacy ? f.detail?.split(' ')[0] || undefined : f.tool;
          const detail = f.legacy ? f.detail?.split(' ').slice(1).join(' ') || undefined : f.detail;
          group.tools.push({ name, detail, key: fkey });
        } else {
          const open = group.tools.find((t) => t.result === undefined && t.name !== undefined);
          if (open) {
            open.result = f.detail ?? f.text.trim();
            open.isError = f.isError;
          } else {
            group.tools.push({ result: f.detail ?? f.text.trim(), isError: f.isError, key: fkey });
          }
        }
        continue;
      }
      flush();
      if (f.kind === 'gate') {
        items.push({ kind: 'gate', text: f.text, isError: f.isError ?? false, ts: f.ts, key: `gate-${fkey}` });
      } else if (f.kind === 'prose' || f.kind === 'thinking' || f.kind === 'stage' || f.kind === 'system') {
        // legacy multi-line splits can produce empty prose fragments (trailing/doubled \n) — drop them
        if (f.legacy && f.kind === 'prose' && f.text.trim() === '') continue;
        items.push({ kind: f.kind, text: f.text, ts: f.ts, key: `${f.kind}-${fkey}` });
      } else {
        // forward-compat: the API may emit kinds this build doesn't know about yet
        // (e.g. a future structured kind); render them as dim system lines instead
        // of silently dropping them.
        items.push({ kind: 'system', text: f.text, ts: f.ts, key: `system-${fkey}` });
      }
    }
  }
  flush();
  return items;
}
